// Budget demo · selectors — the only thing views import from the model.

import { hashSeed, SEGMENT_META, SEGMENT_ORDER } from "./forecast";
import {
    Budget,
    BudgetLine,
    CategoryId,
    Contract,
    DataSource,
    Money,
    Segment,
    Tone,
    usd,
    Workspace,
} from "./types";

// ---------------------------------------------------------------------------
// Lookups & totals
// ---------------------------------------------------------------------------
export function getBudget(ws: Workspace, year: number): Budget | undefined {
    return ws.budgets.find((b) => b.year === year);
}

export function getLines(ws: Workspace, year: number): BudgetLine[] {
    return getBudget(ws, year)?.lines ?? [];
}

/** The fiscal years the workspace holds a budget for, ascending. */
export function availableYears(ws: Workspace): number[] {
    return ws.budgets.map((b) => b.year).sort((a, b) => a - b);
}

const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);

export const planTotal = (lines: BudgetLine[]): Money =>
    usd(sum(lines.map((l) => l.planAmount.amount)));

// ---------------------------------------------------------------------------
// Grouping by segment
// ---------------------------------------------------------------------------
export interface SegmentGroup {
    segment: Segment;
    label: string;
    tone: Tone;
    lines: BudgetLine[];
    plan: Money;
    count: number;
}

export function groupBySegment(lines: BudgetLine[]): SegmentGroup[] {
    return SEGMENT_ORDER.map((segment) => {
        const segLines = lines.filter((l) => l.segment === segment);
        return {
            segment,
            label: SEGMENT_META[segment].label,
            tone: SEGMENT_META[segment].tone,
            lines: segLines,
            plan: planTotal(segLines),
            count: segLines.length,
        };
    }).filter((g) => g.count > 0);
}

// ---------------------------------------------------------------------------
// Tail pool — aggregated live from the below-materiality PO pool, by category.
// The view applies the trend allowance; ingesting POs grows these directly.
// ---------------------------------------------------------------------------
export interface TailSubcategory {
    categoryId: CategoryId;
    categoryName: string;
    vendorCount: number;
    /** Raw pooled annual spend, before any trend allowance. */
    base: number;
}

export function tailPoolByCategory(ws: Workspace): TailSubcategory[] {
    const byCat = new Map<string, TailSubcategory>();
    for (const c of ws.contracts) {
        if (c.material) continue;
        const cur =
            byCat.get(c.categoryId) ??
            {
                categoryId: c.categoryId,
                categoryName: c.categoryName,
                vendorCount: 0,
                base: 0,
            };
        cur.vendorCount += 1;
        cur.base += c.annualSpend.amount;
        byCat.set(c.categoryId, cur);
    }
    return Array.from(byCat.values()).sort((a, b) => b.base - a.base);
}

export interface TailVendor {
    id: string;
    vendorName: string;
    /** Raw pooled annual spend, before any trend allowance. */
    spend: number;
}

/** The individual below-materiality vendors that make up a tail category — used
 * to drill into a category's breakdown. */
export function tailVendorsForCategory(
    ws: Workspace,
    categoryId: string
): TailVendor[] {
    return ws.contracts
        .filter((c) => !c.material && c.categoryId === categoryId)
        .map((c) => ({
            id: c.id,
            vendorName: c.vendorName,
            spend: c.annualSpend.amount,
        }))
        .sort((a, b) => b.spend - a.spend);
}

// ---------------------------------------------------------------------------
// Sources — the records each source brought into the system. The Sources tab
// renders these read-only to show what's been imported / uploaded.
// ---------------------------------------------------------------------------
export function importedRecords(ws: Workspace, source: DataSource): Contract[] {
    if (source.kind === "po_feed") {
        // PO feed pools the below-materiality tail.
        return ws.contracts.filter((c) => !c.material);
    }
    if (!source.live) {
        // Batch upload source.
        return ws.uploadedContracts;
    }
    // Live contract repository (Ariba) — the individually-modeled top of book.
    return ws.contracts.filter((c) => c.material);
}

// ---------------------------------------------------------------------------
// Import timeline — records grouped by the day they landed in the system.
// Day assignment is deterministic (hashed off the contract id) so the timeline
// is stable across renders. Anchored to the demo "today" (Jun 23, 2026).
// ---------------------------------------------------------------------------
const DEMO_TODAY = { year: 2026, month: 5, day: 23 }; // month is 0-based
const MONTHS = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** How many days back a source's imports are spread over. */
function timelineSpan(source: DataSource): number {
    if (source.kind === "po_feed") return 14; // POs trickle in continuously
    if (!source.live) return 3; // a couple of upload batches
    return 6; // repository sync over the past week
}

function dayFromOffset(offset: number): Date {
    return new Date(DEMO_TODAY.year, DEMO_TODAY.month, DEMO_TODAY.day - offset);
}

function dayLabel(d: Date): string {
    return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

function relativeLabel(offset: number): string | null {
    if (offset === 0) return "Today";
    if (offset === 1) return "Yesterday";
    return null;
}

/** A deterministic intraday timestamp (08:00–18:00) for an imported record. */
function importTime(source: DataSource, c: Contract): { mins: number; label: string } {
    const mins = 8 * 60 + (hashSeed(`${source.id}:${c.id}:t`) % (10 * 60));
    const hh = String(Math.floor(mins / 60)).padStart(2, "0");
    const mm = String(mins % 60).padStart(2, "0");
    return { mins, label: `${hh}:${mm}` };
}

export interface ImportRow {
    contract: Contract;
    timeLabel: string;
}

export interface ImportDay {
    offset: number;
    dateLabel: string;
    relativeLabel: string | null;
    rows: ImportRow[];
}

export function importTimeline(source: DataSource, records: Contract[]): ImportDay[] {
    const byDay = new Map<number, Array<{ row: ImportRow; mins: number }>>();
    for (const c of records) {
        const offset = hashSeed(`${source.id}:${c.id}`) % timelineSpan(source);
        const { mins, label } = importTime(source, c);
        const bucket = byDay.get(offset) ?? [];
        bucket.push({ row: { contract: c, timeLabel: label }, mins });
        byDay.set(offset, bucket);
    }
    return Array.from(byDay.entries())
        .sort((a, b) => a[0] - b[0]) // most recent day (offset 0) first
        .map(([offset, items]) => ({
            offset,
            dateLabel: dayLabel(dayFromOffset(offset)),
            relativeLabel: relativeLabel(offset),
            // Within a day, latest imports first.
            rows: items.sort((a, b) => b.mins - a.mins).map((i) => i.row),
        }));
}
